import { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { Series } from "../api/types";

/**
 * График временного ряда.
 *
 * Одна ось значений на график — величины разного масштаба (напряжение и ток)
 * разводятся по отдельным карточкам, а не по двум осям одного графика.
 */
interface Props {
  title: string;
  series: Series[];
  /** Цвета серий в фиксированном порядке — цвет закреплён за фазой, а не за позицией в отфильтрованном списке. */
  colors: string[];
  /** Начинать ось значений от нуля. Для напряжения выключено: иначе колебания сжимаются в линию. */
  fromZero?: boolean;
  digits?: number;
}

interface Point {
  ts: number;
  [metric: string]: number;
}

export function HistoryChart({ title, series, colors, fromZero = false, digits = 1 }: Props) {
  const data = useMemo(() => mergeSeries(series), [series]);
  const unit = series[0]?.unit ?? "";
  const showLegend = series.length >= 2;

  // Ширину оси считаем по самой длинной подписи: у накопительной энергии
  // значения пятизначные, и фиксированной ширины не хватает — подписи обрезаются.
  const yWidth = useMemo(() => {
    let widest = 0;
    for (const s of series) {
      for (const v of s.values) {
        widest = Math.max(widest, formatNumber(v, digits).length);
      }
    }
    return Math.min(96, Math.max(44, widest * 7 + 12));
  }, [series, digits]);

  if (!data.length) {
    return (
      <div className="chart-card">
        <header>
          <h3>{title}</h3>
        </header>
        <div className="chart-empty">Нет данных за выбранный период</div>
      </div>
    );
  }

  return (
    <div className="chart-card">
      <header>
        <h3>{title}</h3>
        {unit && <span className="muted">{unit}</span>}
      </header>

      <ResponsiveContainer width="100%" height={220}>
        <LineChart data={data} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
          {/* Сетка приглушена: она ориентир, а не содержание */}
          <CartesianGrid stroke="var(--grid)" strokeDasharray="0" vertical={false} />
          <XAxis
            dataKey="ts"
            type="number"
            scale="time"
            domain={["dataMin", "dataMax"]}
            tickFormatter={formatTime}
            stroke="var(--axis)"
            tick={{ fill: "var(--text-muted)", fontSize: 11 }}
            tickLine={false}
            minTickGap={40}
          />
          <YAxis
            domain={fromZero ? [0, "auto"] : ["auto", "auto"]}
            stroke="var(--axis)"
            tick={{ fill: "var(--text-muted)", fontSize: 11 }}
            tickLine={false}
            axisLine={false}
            width={yWidth}
            tickFormatter={(v: number) => formatNumber(v, digits)}
          />
          <Tooltip
            content={<ChartTooltip digits={digits} unit={unit} />}
            cursor={{ stroke: "var(--axis)", strokeWidth: 1 }}
          />
          {showLegend && (
            <Legend
              verticalAlign="top"
              align="right"
              height={24}
              iconType="plainline"
              iconSize={14}
              formatter={(value) => <span style={{ color: "var(--text-secondary)", fontSize: 12 }}>{value}</span>}
            />
          )}
          {series.map((s, i) => (
            <Line
              key={s.metric}
              type="monotone"
              dataKey={s.metric}
              name={legendLabel(s.label)}
              stroke={colors[i % colors.length]}
              strokeWidth={2}
              dot={false}
              // Точка под курсором — 8px в диаметре, чтобы попадать по ней мышью
              activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--surface-1)" }}
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Подпись серии в легенде и подсказке.
 *
 * Для фазных величин оставляем только «L1»/«L2»/«L3»: заголовок карточки уже
 * называет величину, а три полные подписи не помещаются в узкий график
 * и наезжают на область данных.
 */
function legendLabel(label: string): string {
  const m = /\b(L[123])$/.exec(label);
  return m ? m[1] : label;
}

/** Сводит параллельные массивы нескольких метрик в общий набор точек по времени. */
function mergeSeries(series: Series[]): Point[] {
  const byTs = new Map<number, Point>();
  for (const s of series) {
    for (let i = 0; i < s.ts.length; i++) {
      const t = s.ts[i];
      let p = byTs.get(t);
      if (!p) {
        p = { ts: t };
        byTs.set(t, p);
      }
      p[s.metric] = s.values[i];
    }
  }
  return [...byTs.values()].sort((a, b) => a.ts - b.ts);
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ dataKey: string; name: string; value: number; color: string }>;
  label?: number;
  digits: number;
  unit: string;
}

function ChartTooltip({ active, payload, label, digits, unit }: TooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="tooltip">
      <div className="t-time">{formatDateTime(label ?? 0)}</div>
      {payload.map((p) => (
        <div className="t-row" key={p.dataKey}>
          <span className="swatch" style={{ background: p.color }} aria-hidden="true" />
          <span>{p.name}</span>
          <span className="t-value">
            {formatNumber(p.value, digits)}
            {unit && ` ${unit}`}
          </span>
        </div>
      ))}
    </div>
  );
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

function formatDateTime(ts: number): string {
  return new Date(ts).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatNumber(v: number, digits: number): string {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return v.toLocaleString("ru-RU", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
